/* eslint-disable max-lines */
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  ISurveyQuestionTypenames,
  LeaveChatResultTypenames,
  Mutation,
  SurveyQuestionOption,
} from '@varo/graphql-schema-types';
import { useFocusEffect, useRouter } from 'expo-router';
import {
  getIsStarFilled,
  getMergedSurveyResponse,
  SelectedStarRating,
  SurveyResponse,
} from 'features/chat/helpers/LeaveChat.helpers';
import useChatSurvey from 'features/chat/hooks/useChatSurvey';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler, Keyboard, StyleSheet, View } from 'react-native';
import LeaveChatSession from 'services/graphql/mutations/Chat/LeaveChatSession';
import SubmitSurvey from 'services/graphql/mutations/Chat/SubmitSurvey';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import PillGroup, {
  PillConfigProps,
} from 'shared/components/vxl/navigation/PillGroup/PillGroup';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface LeaveChatProps {
  chatSessionId: string;
  isChatEndedFromEvent: boolean;
}

const LeaveChat = ({ chatSessionId, isChatEndedFromEvent }: LeaveChatProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('chatStrings', {
    keyPrefix: 'leaveChat',
  });
  const { isFeatureEnabled } = useStatsig();
  const isDecagonChatEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_DECAGON_CHAT,
  );

  const [skipSurvey, setSkipSurvey] = useState(false);
  const [rating, setRating] = useState<SelectedStarRating[]>([]);
  const [hoverRating, setHoverRating] = useState<SelectedStarRating[]>([]);
  const { clearSessionIdAfterSurvey, endChatLocally } = useContext(ChatContext);
  const [bottomSheetStatus, setBottomSheetStatus] = useState({
    isConfirmationBottomSheetOpen: false,
    isSurveyBottomSheetOpen: false,
  });
  const [thumbsUpDownSurveyResponse, setThumbsUpDownSurveyResponse] = useState<
    SurveyResponse[]
  >([]);
  const [starSurveyResponse, setStarSurveyResponse] = useState<
    SurveyResponse[]
  >([]);

  const [isChatEnded, setIsChatEnded] = useState(false);
  const confirmationBottomSheetRef = useRef<BottomSheetModal>(null);
  const surveyBottomSheetRef = useRef<BottomSheetModal>(null);
  const {
    surveyQuestions,
    fetching: surveyQuestionsFetching,
    setQuestions,
  } = useChatSurvey(
    skipSurvey || isChatEnded || isChatEndedFromEvent,
    chatSessionId,
  );

  const [
    { data: leaveChatResponse, fetching: leaveChatFetching },
    leaveChatSession,
  ] = useCancellableMutation<Mutation>(LeaveChatSession);

  const [{ fetching: submitSurveyFetching }, submitSurvey] =
    useCancellableMutation<Mutation>(SubmitSurvey);

  const openConfirmationBottomSheet = () => {
    Keyboard.dismiss();
    confirmationBottomSheetRef.current?.present();
    setBottomSheetStatus((data) => ({
      ...data,
      isConfirmationBottomSheetOpen: true,
    }));
  };

  useEffect(() => {
    if (!isChatEndedFromEvent) {
      setQuestions([]);
      setSkipSurvey(false);
    }
  }, [isChatEndedFromEvent, setQuestions]);

  const closeConfirmationBottomSheet = () => {
    confirmationBottomSheetRef.current?.close();
    setBottomSheetStatus((data) => ({
      ...data,
      isConfirmationBottomSheetOpen: false,
    }));
  };

  // Close the confirmation modal without altering isChatEndedFromEvent.
  const dismissConfirmationModal = () => {
    confirmationBottomSheetRef.current?.close();
    setBottomSheetStatus((data) => ({
      ...data,
      isConfirmationBottomSheetOpen: false,
    }));
  };

  const openSurveyBottomSheet = useCallback(() => {
    if (!bottomSheetStatus.isSurveyBottomSheetOpen) {
      setRating([]);
      setHoverRating([]);
      setStarSurveyResponse([]);
      setThumbsUpDownSurveyResponse([]);
      surveyBottomSheetRef.current?.present();
      setBottomSheetStatus((data) => ({
        ...data,
        isSurveyBottomSheetOpen: true,
      }));
      setIsChatEnded(true);
    }
  }, [bottomSheetStatus.isSurveyBottomSheetOpen]);

  const closeSurveyBottomSheet = () => {
    if (surveyBottomSheetRef.current?.dismiss) {
      surveyBottomSheetRef.current.dismiss();
    } else {
      surveyBottomSheetRef.current?.close();
    }
    setBottomSheetStatus((data) => ({
      ...data,
      isSurveyBottomSheetOpen: false,
    }));
    clearSessionIdAfterSurvey();
  };

  const handleDismissSurveyAnalytics = usePressableAnalytics({
    analyticsId: 'dismissSurvey',
    customAttributes: {
      chat_session_id: chatSessionId,
    },
  });

  const handleBackPress = useCallback(() => {
    if (bottomSheetStatus.isConfirmationBottomSheetOpen) {
      confirmationBottomSheetRef.current?.close();
      closeConfirmationBottomSheet();
    } else if (bottomSheetStatus.isSurveyBottomSheetOpen) {
      closeSurveyBottomSheet();
      handleDismissSurveyAnalytics();
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bottomSheetStatus.isConfirmationBottomSheetOpen,
    bottomSheetStatus.isSurveyBottomSheetOpen,
    handleDismissSurveyAnalytics,
  ]);

  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => backHandler.remove();
    }, [handleBackPress]),
  );

  //Handle star ratings on hover effects
  const handleStarPress = (value: number, questionId: string) => {
    setRating((prevResponses) => {
      const index = prevResponses.findIndex(
        (response) => response.questionId === questionId,
      );
      if (index !== -1) {
        const updatedResponses = [...prevResponses];
        updatedResponses[index].stars = value;
        return updatedResponses;
      }
      return [...prevResponses, { questionId, stars: value }];
    });
  };

  const handleStarHover = (value: number, questionId: string) => {
    setHoverRating([{ questionId, stars: value }]);
  };

  const handleStarHoverOut = (questionId: string) => {
    setHoverRating((ratings) =>
      ratings.filter((element) => element.questionId !== questionId),
    );
  };

  const pushSurveyResponse = (
    selectedRating: SurveyResponse,
    responseType: string,
  ) => {
    if (responseType === 'star') {
      setStarSurveyResponse((prevResponses) =>
        getMergedSurveyResponse(prevResponses, selectedRating),
      );
    }
    if (responseType === 'thumbsUpDown') {
      setThumbsUpDownSurveyResponse((prevResponses) =>
        getMergedSurveyResponse(prevResponses, selectedRating),
      );
    }
  };

  const handleChatSessionEndedAnalytics = usePressableAnalytics({
    analyticsId: 'chatSessionEnded',
    customAttributes: {
      chat_session_id: chatSessionId,
    },
  });

  const handleLeaveChat = async () => {
    try {
      // Close the confirmation modal UI without toggling end flags.
      dismissConfirmationModal();
      const result = await leaveChatSession({
        idempotencyId: getUuid(),
        input: {
          isDecagon: isDecagonChatEnabled,
        },
      });
      // End the session locally when mutation succeeds.
      // For Decagon chats especially, a `SessionEnded` event may not be emitted
      // from the backend, so we must update the UI state immediately.
      // Setting isChatEndedFromEvent prevents useChatHistory from overriding these values.
      // Note: We don't clear chatSessionId here - it's cleared after the survey is
      // completed/dismissed in closeSurveyBottomSheet. This keeps LeaveChat mounted
      // so the survey can be shown.
      if (
        result.data?.leaveChat?.__typename ===
        LeaveChatResultTypenames.LeaveChatSuccess
      ) {
        endChatLocally();
      }
      handleChatSessionEndedAnalytics();
      setIsChatEnded(true);
      dismissConfirmationModal();
    } catch (e) {
      logErrorWithContext(e, {
        featureArea: FeatureArea.CUSTOMER_SERVICING_SUPPORT,
        responsibleTeam: ResponsibleTeam.CUSTOMER_SUPPORT,
        severity: ErrorSeverity.CRITICAL,
      });
    }
  };

  const handleSubmitSurvey = async () => {
    try {
      const response = await submitSurvey({
        input: {
          referenceId: chatSessionId,
          feedback: [...starSurveyResponse, ...thumbsUpDownSurveyResponse],
        },
      });
      if (response.data) {
        closeSurveyBottomSheet();
        if (router.canGoBack()) {
          router.back();
        } else {
          router.push('/myvaro/help');
        }
      }
    } catch (e) {
      logErrorWithContext(e, {
        featureArea: FeatureArea.CUSTOMER_SERVICING_SUPPORT,
        responsibleTeam: ResponsibleTeam.CUSTOMER_SUPPORT,
        severity: ErrorSeverity.CRITICAL,
      });
    }
  };

  const submitStarRatings = (
    option: SurveyQuestionOption,
    questionId: string,
  ) => {
    handleStarPress(parseInt(option.value), questionId);
    pushSurveyResponse(
      {
        questionId,
        optionValue: option.value,
      },
      'star',
    );
  };

  useEffect(() => {
    if (
      leaveChatResponse?.leaveChat?.__typename ===
      LeaveChatResultTypenames.LeaveChatError
    ) {
      logErrorWithContext(
        `LeaveChat error: ${leaveChatResponse.leaveChat.title}: ${leaveChatResponse.leaveChat.message}`,
        {
          featureArea: FeatureArea.CUSTOMER_SERVICING_SUPPORT,
          responsibleTeam: ResponsibleTeam.CUSTOMER_SUPPORT,
          severity: ErrorSeverity.LOW,
        },
      );
    }
    if (
      leaveChatResponse?.leaveChat?.__typename ===
      LeaveChatResultTypenames.LeaveChatSuccess
    ) {
      setSkipSurvey(true);

      if (isChatEnded && !surveyQuestionsFetching && surveyQuestions) {
        openSurveyBottomSheet();
      }
    }
    setSkipSurvey(false);
  }, [
    leaveChatResponse,
    isChatEnded,
    surveyQuestionsFetching,
    surveyQuestions,
    openSurveyBottomSheet,
    isChatEndedFromEvent,
  ]);

  useEffect(() => {
    if (chatSessionId) {
      setIsChatEnded(false);
      setBottomSheetStatus((data) => ({
        ...data,
        isSurveyBottomSheetOpen: false,
      }));
    }
  }, [chatSessionId]);

  useFocusEffect(
    useCallback(() => {
      if (surveyQuestions?.length && isChatEndedFromEvent) {
        void openSurveyBottomSheet();
      }
    }, [isChatEndedFromEvent, openSurveyBottomSheet, surveyQuestions?.length]),
  );

  const lastQuestionIndex = (surveyQuestions?.length ?? 0) - 1;

  const isSubmitButtonDisabled =
    (surveyQuestions &&
      starSurveyResponse?.length + thumbsUpDownSurveyResponse?.length <
        surveyQuestions.length) ||
    submitSurveyFetching;

  return (
    <View style={styles.leaveChat}>
      {!isChatEnded ? (
        <Button
          label={translatedText('buttonLabel')}
          onPress={openConfirmationBottomSheet}
          type="TEXT"
          isLoading={leaveChatFetching || surveyQuestionsFetching}
          isDisabled={leaveChatFetching}
          analyticsId="endChatClicked"
          customAttributes={{ chat_session_id: chatSessionId }}
        />
      ) : null}
      <BottomSheet ref={confirmationBottomSheetRef}>
        <View style={styles.confirmationSheetContainer}>
          <TitleMdText numberOfLines={0}>
            {translatedText('leaveChatConfirmation.title')}
          </TitleMdText>
          <BodyBaseText numberOfLines={0}>
            {translatedText('leaveChatConfirmation.description')}
          </BodyBaseText>
        </View>
        <View style={styles.confirmationSheetContainer}>
          <Button
            label={translatedText('leaveChatConfirmation.buttons.submit')}
            onPress={handleLeaveChat}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
            isDisabled={leaveChatFetching}
          />
          <Button
            label={translatedText('leaveChatConfirmation.buttons.cancel')}
            onPress={closeConfirmationBottomSheet}
            size="LARGE"
            type="SECONDARY"
            width="FULL"
            isDisabled={leaveChatFetching}
          />
        </View>
      </BottomSheet>
      <BottomSheet
        ref={surveyBottomSheetRef}
        onDismiss={() => {
          setIsChatEnded(true);
          handleDismissSurveyAnalytics();
        }}
      >
        <View style={styles.surveySheet}>
          {surveyQuestionsFetching ? (
            <View style={styles.loader}>
              <LoadingSpinner />
            </View>
          ) : (
            <>
              {surveyQuestions?.map((question, index) => {
                return (
                  <React.Fragment key={question?.id}>
                    <BodyBaseBoldText
                      style={styles.surveyTitle}
                      numberOfLines={0}
                      key={question?.id}
                    >
                      {question?.title}
                    </BodyBaseBoldText>
                    {question?.__typename ===
                    ISurveyQuestionTypenames.StarRatingSurveyQuestion ? (
                      <View style={styles.starRatings}>
                        {question?.options?.map((option) => {
                          return (
                            <IconButton
                              iconOnPress={() =>
                                submitStarRatings(option, question?.id)
                              }
                              iconColor={
                                getIsStarFilled(
                                  option.value,
                                  question?.id,
                                  rating,
                                  hoverRating,
                                )
                                  ? theme.color.interactive.primary
                                  : undefined
                              }
                              key={option.id}
                              aria-label="STAR"
                              iconKey={
                                getIsStarFilled(
                                  option.value,
                                  question?.id,
                                  rating,
                                  hoverRating,
                                )
                                  ? 'STAR_FILLED'
                                  : 'STAR'
                              }
                              iconButtonAccessibilityLabel="star"
                              onHover={() =>
                                handleStarHover(
                                  parseInt(option.value),
                                  question?.id,
                                )
                              }
                              onHoverOut={() =>
                                handleStarHoverOut(question?.id)
                              }
                              hoverColor={theme.color.interactive.primary}
                              focusedColor={theme.color.interactive.primary}
                              testID={
                                getIsStarFilled(
                                  option.value,
                                  question?.id,
                                  rating,
                                  hoverRating,
                                )
                                  ? 'star_filled'
                                  : index + '_' + 'star'
                              }
                            />
                          );
                        })}
                      </View>
                    ) : null}
                    {question?.__typename ===
                    ISurveyQuestionTypenames.ThumbsUpOrDownSurveyQuestion ? (
                      <View style={styles.issueResolvedSection}>
                        <View style={styles.issueResolvedButton}>
                          <PillGroup
                            onSelection={(param: PillConfigProps) => {
                              pushSurveyResponse(
                                {
                                  questionId: question?.id as string,
                                  optionValue: param.labelText,
                                },
                                'thumbsUpDown',
                              );
                            }}
                            aria-label="THUMBS_UP_THUMBS_DOWN"
                            pillConfigs={[
                              {
                                id: question?.thumbsUpOption?.id,
                                labelText:
                                  question?.thumbsUpOption?.title || '',
                                iconKey: 'THUMBS_UP',
                                testID: index + '_' + 'thumbs_up',
                              },
                              {
                                id: question?.thumbsDownOption?.id,
                                labelText:
                                  question?.thumbsDownOption?.title || '',
                                iconKey: 'THUMBS_DOWN',
                                testID: index + '_' + 'thumbs_down',
                              },
                            ]}
                          />
                        </View>
                      </View>
                    ) : null}
                    {!(index === lastQuestionIndex) ? (
                      <HairLineDivider />
                    ) : null}
                  </React.Fragment>
                );
              })}

              <View style={styles.submitFeedbackButton}>
                <Button
                  type="PRIMARY"
                  label={translatedText('chatSurvey.buttonTitle')}
                  onPress={handleSubmitSurvey}
                  isLoading={submitSurveyFetching}
                  analyticsId="submitFeedbackClicked"
                  customAttributes={{
                    chat_ratings: starSurveyResponse ?? '',
                    chat_session_id: chatSessionId,
                    satisfaction_response: thumbsUpDownSurveyResponse ?? '',
                  }}
                  isDisabled={isSubmitButtonDisabled}
                />
              </View>
            </>
          )}
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  confirmationSheetContainer: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  issueResolvedButton: {
    gap: 12,
  },
  issueResolvedSection: {
    alignItems: 'center',
    gap: theme.spacing.m,
  },
  leaveChat: {
    alignSelf: 'flex-end',
  },
  loader: {
    alignItems: 'center',
  },
  starRatings: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  submitFeedbackButton: {
    paddingTop: theme.spacing.m,
  },
  surveySheet: {
    gap: theme.spacing.m,
    height: '100%',
    justifyContent: 'center',
    overflow: 'visible',
    padding: theme.spacing.s,
  },
  surveyTitle: {
    textAlign: 'center',
  },
});

export default LeaveChat;
